import React, { useCallback, useEffect, useState } from 'react';
import lodash from 'lodash';
import { Redirect } from 'react-router-dom';

import initialFormData from 'constants/initialFormData';

import { EMPLOYEE_PROFILE } from 'constants/routes';
import { DATE_FORMAT_DEFAULT } from 'constants/appConstants';
import { USER_UPDATABLE_FIELDS } from 'constants/updatableFields';
import { EMPLOYEES, USER_PERMISSIONS } from 'constants/permissions';

import * as toast from 'utils/toast';
import { interpolate, extractFullName } from 'utils/string';
import * as userService from 'services/user';
import { getFormattedDate } from 'utils/date';
import { handleError } from 'utils/errorHandler';

import * as roleService from 'services/role';
import * as departmentService from 'services/department';
import * as designationService from 'services/designation';
import * as designationAreaService from 'services/designationArea';
import * as engagementStatusService from 'services/engagementStatus';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import { filterAllowedRoles } from 'utils/user';
import Loading from 'components/common/loading/Loading';
import EmployeeForm from '../components/employeeForm/EmployeeForm';

import type {
  AppraiserAllocation,
  CreateUpdateForm,
  LeapfrogHistory,
  ProfileInfo,
  UserPayloadInput,
} from 'interface/createUpdateForm';
import type { Role } from 'interface/role';

interface NamedEntity {
  id: number;
  name: string;
}

interface PermissionRole {
  id: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
}

interface AvailabilityTimeValue {
  value?: string | null;
  type?: string | null;
}

interface EmployeeHistoryItem {
  id?: number;
  transitionDate?: string | Date | null;
}

interface EmployeeImagePreview {
  data_url: string;
  file: {
    name: string;
  };
}

interface EmployeeWithProfile extends Record<string, string | number | boolean | null | undefined | object> {
  id: number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email?: string;
  avatarUrl?: string | null;
  supervisor?: { id?: number | null } | null;
  leaveIssuer?: { id?: number | null } | null;
  teamManager?: { id?: number | null } | null;
  coach?: { id?: number | null } | null;
  department?: { id?: number | null } | null;
  roles?: Role[];
  pastExperience?: number | null;
  availabilityTime?: {
    startTime?: string | null;
    startTimeType?: string | null;
    endTime?: string | null;
    endTimeType?: string | null;
  };
  empStatusHistory?: EmployeeHistoryItem[];
  designationAreaHistory?: EmployeeHistoryItem[];
}

interface RedirectTarget {
  pageUrl: string;
  params: {
    id: number | null;
  };
}

interface PermissionsMap {
  [key: string]: boolean;
}

interface CurrentUser {
  id: number;
}

interface UpdateFormOwnProps {
  empId: string;
  user: CurrentUser;
}

type UpdateFormProps = UpdateFormOwnProps;
type EmployeeFormValues = CreateUpdateForm & ProfileInfo & LeapfrogHistory & AppraiserAllocation;
type FormDataShape = {
  employeeImage?: EmployeeImagePreview[] | Array<{ file?: File }> | null;
  availabilityStartTime?: AvailabilityTimeValue | { hours: number; minutes: number };
  availabilityEndTime?: AvailabilityTimeValue | { hours: number; minutes: number };
  joinDate?: string | Date | null;
  username?: string | null;
  roleIds?: number[];
  pastExperienceYears?: number | null;
  pastExperienceMonths?: number | null;
  supervisorId?: number | null;
  leaveIssuerId?: number | null;
  teamManagerId?: number | null;
  coachId?: number | null;
  departmentId?: number | null;
  empStatusHistory?: EmployeeHistoryItem[];
  designationAreaHistory?: EmployeeHistoryItem[];
  middleName?: string | null;
  [key: string]:
    | string
    | Date
    | number
    | boolean
    | null
    | undefined
    | AvailabilityTimeValue
    | number[]
    | EmployeeImagePreview[]
    | EmployeeHistoryItem[]
    | Array<{ file?: File }>
    | { hours: number; minutes: number };
};

const UpdateForm: React.FC<UpdateFormProps> = ({ empId, user }) => {
  const permissions = useUserPermissions() as PermissionsMap;
  const [employees, setEmployees] = useState<EmployeeWithProfile[]>([]);
  const [departments, setDepartments] = useState<NamedEntity[]>([]);
  const [designations, setDesignations] = useState<NamedEntity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [designationAreas, setDesignationAreas] = useState<NamedEntity[]>([]);
  const [engagementStatus, setEngagementStatus] = useState<NamedEntity[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithProfile | null>(null);
  const [formData, setFormData] = useState<FormDataShape>(initialFormData as FormDataShape);
  const [roles, setRoles] = useState<Role[]>([]);
  const [toPage, setToPage] = useState<RedirectTarget | null>(null);

  const fetchEmployee = useCallback(async (id: string): Promise<EmployeeWithProfile> => {
    return (await userService.fetchById(id)) as EmployeeWithProfile;
  }, []);

  /**
   * Fetchs departments.
   *
   * @returns {Array}
   */
  const fetchDepartments = useCallback(async (): Promise<NamedEntity[]> => {
    const response = await departmentService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  /**
   * Fetchs designations.
   *
   * @returns {Array}
   */
  const fetchDesignations = useCallback(async (): Promise<NamedEntity[]> => {
    const response = await designationService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  /**
   * Fetches Areas.
   *
   * @returns {Array}
   */
  const fetchDesignationAreas = useCallback(async (): Promise<NamedEntity[]> => {
    const response = await designationAreaService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  /**
   * Fetches Engagement status.
   *
   * @returns {Array}
   */
  const fetchEngagementStatus = useCallback(async (): Promise<NamedEntity[]> => {
    const response = await engagementStatusService.fetchAll();
    const { data } = response as { data: Array<{ id: number; name: string }> };
    return data.map((item) => ({ id: item.id, name: item.name }));
  }, []);

  /**
   *
   * @returns {Array} roles data
   */
  const fetchRoles = useCallback(async (): Promise<PermissionRole[]> => {
    const data = await roleService.fetchAll();
    if (!Array.isArray(data)) {
      return [];
    }
    return data.map((role) => ({ id: String(role.id), name: role.name }));
  }, []);

  const populateFormData = useCallback((employee: EmployeeWithProfile | null): FormDataShape => {
    const newFormData: FormDataShape = { ...(initialFormData as FormDataShape) };

    if (!employee) return newFormData;

    for (const key in newFormData) {
      if (key.startsWith('is')) {
        newFormData[key] = employee[key] ? 1 : 0;
      } else {
        const employeeValue = employee[key];
        if (
          typeof employeeValue === 'string' ||
          typeof employeeValue === 'number' ||
          typeof employeeValue === 'boolean' ||
          employeeValue === null ||
          employeeValue === undefined
        ) {
          newFormData[key] = employeeValue;
        }
      }
    }

    const joinDate = employee.joinDate as string | Date | null | undefined;

    newFormData.joinDate = (joinDate && getFormattedDate(joinDate, DATE_FORMAT_DEFAULT)) || '';

    newFormData.supervisorId = employee.supervisor?.id || null;
    newFormData.leaveIssuerId = employee.leaveIssuer?.id || null;
    newFormData.teamManagerId = employee.teamManager?.id || null;
    newFormData.coachId = employee.coach?.id || null;
    newFormData.departmentId = employee.department?.id || null;
    newFormData.username = employee.email || '';
    newFormData.roleIds = (employee.roles || [])
      .map((item) => (typeof item.id === 'number' ? item.id : Number(item.id)))
      .filter((value) => Number.isFinite(value));

    if (employee.pastExperience) {
      newFormData.pastExperienceYears = Math.floor((employee.pastExperience || 0) / 12);
      newFormData.pastExperienceMonths = (employee.pastExperience || 0) % 12;
    }

    newFormData.availabilityStartTime = {
      value: employee.availabilityTime?.startTime,
      type: employee.availabilityTime?.startTimeType,
    };

    newFormData.availabilityEndTime = {
      value: employee.availabilityTime?.endTime,
      type: employee.availabilityTime?.endTimeType,
    };

    newFormData.empStatusHistory = lodash.orderBy(
      employee.empStatusHistory,
      ['transitionDate', 'id'],
      ['desc', 'desc']
    );

    newFormData.designationAreaHistory = lodash.orderBy(
      employee.designationAreaHistory,
      ['transitionDate', 'id'],
      ['desc', 'desc']
    );

    if (employee.avatarUrl) {
      newFormData.employeeImage = [
        {
          data_url: employee.avatarUrl,
          file: { name: employee.avatarUrl.split('/').slice(-1)[0].split('?')[0] || '' },
        },
      ];
    }

    return newFormData;
  }, []);

  const handleUpdateClick = useCallback(
    async (submittedFormData: UserPayloadInput) => {
      setIsSubmitting(true);

      const submittedData = submittedFormData as UserPayloadInput & {
        employeeImage?: File | EmployeeImagePreview[] | Array<{ file?: File }> | null;
      };
      const { employeeImage: employeeImageInput, ...updateFormData } = submittedData;
      const parsedEmployeeImage = employeeImageInput as File | EmployeeImagePreview[] | Array<{ file?: File }> | null;
      const employeeImage = Array.isArray(parsedEmployeeImage)
        ? parsedEmployeeImage[0]?.file || null
        : parsedEmployeeImage;

      const selectedEmployeeId = selectedEmployee?.id;
      const userJson = {
        ...updateFormData,
        id: selectedEmployeeId,
        middleName: updateFormData.middleName || null,
      };

      try {
        let updatedUser: EmployeeWithProfile = {} as EmployeeWithProfile;

        const { data } = (await userService.update(userJson, userJson.id)) as { data: EmployeeWithProfile };

        updatedUser = { ...data };

        const updatedUserId = data?.id || null;

        if (updatedUserId && employeeImage instanceof File) {
          const imageUploadFormData = new FormData();
          imageUploadFormData.append('employeeImage', employeeImage);

          const { data: imageUploadData } = (await userService.upload(updatedUserId, imageUploadFormData)) as {
            data: { avatarUrl?: string };
          };
          updatedUser.avatarUrl = imageUploadData.avatarUrl || null;
        }

        toast.success({
          title: 'Success',
          message: 'Profile has been updated successfully.',
        });

        setToPage({
          pageUrl: EMPLOYEE_PROFILE,
          params: {
            id: updatedUserId,
          },
        });

        const newEmployees = employees.reduce<EmployeeWithProfile[]>((acc, cur) => {
          if (cur.id !== userJson.id) {
            return [...acc, cur];
          }

          return [...acc, updatedUser];
        }, []);

        setEmployees(newEmployees);
      } catch (error) {
        handleError(error);
      }

      setIsSubmitting(false);
    },
    [employees, selectedEmployee]
  );

  const canEdit = useCallback(
    (key: string): boolean => {
      const isSelf = +empId === user.id;
      const isSuperAdmin = permissions[USER_PERMISSIONS.CREATE_SUPER_ADMIN];

      if (key === 'roleIds') {
        const allowedRoleIds = filterAllowedRoles(
          roles.map((role) => ({
            id: String(role.id ?? ''),
            name: role.name || '',
          })),
          permissions
        ).reduce<Record<string, string>>(
          (prevValue, currentValue) => ({
            ...prevValue,
            [String(currentValue.id)]: String(currentValue.id),
          }),
          {}
        );

        return isSuperAdmin || (!isSelf && (formData.roleIds || []).every((roleId) => allowedRoleIds[String(roleId)]));
      }

      return isSelf && !isSuperAdmin ? USER_UPDATABLE_FIELDS.includes(key) : permissions[EMPLOYEES.PROFILE.UPDATE];
    },
    [empId, formData.roleIds, permissions, roles, user.id]
  );

  const getPageTitle = useCallback((employeeToShow: EmployeeWithProfile | null): string => {
    const employeeFullName = employeeToShow
      ? extractFullName({
          firstName: employeeToShow.firstName || '',
          middleName: employeeToShow.middleName || '',
          lastName: employeeToShow.lastName || '',
        })
      : '';
    return employeeFullName ? `${employeeFullName}'s profile edit | Vyaguta` : 'Profile edit | Vyaguta';
  }, []);

  useEffect(() => {
    window.document.title = 'Profile edit | Vyaguta';

    const fetchAndSetData = async () => {
      try {
        const [designationsData, departmentsData, designationAreasData, engagementStatusData, rolesData] =
          await Promise.all([
            fetchDesignations(),
            fetchDepartments(),
            fetchDesignationAreas(),
            fetchEngagementStatus(),
            fetchRoles(),
          ]);

        const toShow = await fetchEmployee(empId);
        const userRolesResponse = (await userService.fetchRoles(empId)) as {
          roles?: Array<{ id?: number | string; name?: string }>;
        };
        const userRoles = (userRolesResponse.roles || []).map((role) => ({
          id: typeof role.id === 'number' ? role.id : Number(role.id),
          name: role.name,
        }));

        window.document.title = getPageTitle(toShow);

        setSelectedEmployee(toShow);
        setFormData(populateFormData({ ...toShow, roles: userRoles }));
        setDepartments(departmentsData);
        setDesignations(designationsData);
        setDesignationAreas(designationAreasData);
        setEngagementStatus(engagementStatusData);
        const allowedRoles = filterAllowedRoles(rolesData, permissions).map((role) => ({
          id: Number(role.id),
          name: typeof role.name === 'string' ? role.name : '',
        }));
        setRoles(lodash.uniqBy([...allowedRoles, ...userRoles], 'id'));
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchAndSetData();
  }, [
    empId,
    fetchDepartments,
    fetchDesignationAreas,
    fetchDesignations,
    fetchEmployee,
    fetchEngagementStatus,
    fetchRoles,
    getPageTitle,
    permissions,
    populateFormData,
  ]);

  if (toPage) {
    const togo = interpolate(toPage.pageUrl, toPage.params);
    return <Redirect to={togo} />;
  }

  return isLoading ? (
    <div className="container loading-container">
      <Loading />
    </div>
  ) : (
    <div>
      {selectedEmployee && (
        <EmployeeForm
          isSubmitting={isSubmitting}
          formData={formData as EmployeeFormValues & FormDataShape}
          isCreateForm={false}
          canEdit={canEdit}
          handleSubmit={handleUpdateClick}
          departments={departments}
          designations={designations}
          designationAreas={designationAreas}
          engagementStatus={engagementStatus}
          roles={roles}
          empId={empId}
          employees={employees}
          skills={[]}
        />
      )}
    </div>
  );
};

export default UpdateForm;
